import React from "react";
import { Link } from "react-router-dom";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";
import { useState } from "react";
import { FaAsterisk } from "react-icons/fa";
import authService from "../services/authService";

const inputClassName =
  "w-full rounded-xl border border-slate-700/80 bg-slate-950/80 px-4 py-3 text-sm text-slate-50 outline-none transition placeholder:text-slate-500 focus:border-blue-400/60 focus:ring-2 focus:ring-blue-500/20";

const labelClassName =
  "mb-2 flex items-center gap-1 text-sm font-semibold text-slate-200";

const ForgetPass = () => {
  const [email, setEmail] = useState("");
  const [captcha, setCaptcha] = useState(() =>
    Math.floor(1000 + Math.random() * 9000)
  );
  const [captchaInput, setCaptchaInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const refreshCaptcha = () => {
    setCaptcha(Math.floor(1000 + Math.random() * 9000));
    setCaptchaInput("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    // Validate captcha
    if (captchaInput !== captcha.toString()) {
      setError("Invalid captcha. Please try again.");
      refreshCaptcha();
      return;
    }

    setLoading(true);

    try {
      const response = await authService.forgotPassword(email);
      
      if (response.status === "success") {
        setSuccess(response.message || "Password reset link sent! Please check your email.");
        setEmail("");
        setCaptchaInput("");
        refreshCaptcha();
      }
    } catch (err) {
      setError(err.message || "Failed to send reset link. Please try again.");
      refreshCaptcha();
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#111318] text-slate-200">
        <Header />

        <section className="flex justify-center px-4 py-8 sm:px-6 sm:py-10 lg:px-8 lg:py-12">
          <div className="w-full max-w-[560px]">
            <form
              className="rounded-[24px] border border-slate-700/70 bg-[linear-gradient(180deg,rgba(17,24,39,0.96),rgba(15,23,42,0.92))] p-5 shadow-[0_22px_60px_rgba(0,0,0,0.35)] backdrop-blur-[10px] sm:p-8"
              onSubmit={handleSubmit}
            >
              <div className="mb-6 text-center sm:mb-7">
                <h2 className="text-2xl font-bold tracking-[-0.02em] text-slate-50 sm:text-[30px]">
                  Forgot Password?
                </h2>
                <p className="mt-2 text-sm leading-6 text-slate-400">
                Please enter your email address. You will receive a link to
                create a new password via email.
                </p>
              </div>

              {error && (
                <div className="mb-5 rounded-2xl border border-rose-400/35 bg-rose-500/10 px-4 py-3 text-center text-sm text-rose-100">
                  {error}
                </div>
              )}

              {success && (
                <div className="mb-5 rounded-2xl border border-emerald-400/35 bg-emerald-500/10 px-4 py-3 text-center text-sm text-emerald-100">
                  {success}
                </div>
              )}

              <div className="mb-4">
                <label htmlFor="forgot-email" className={labelClassName}>
                  Email <FaAsterisk className="mt-0.5 text-[8px] text-rose-400" aria-hidden="true" />
                </label>
                <input
                  id="forgot-email"
                  type="email"
                  placeholder="Email"
                  className={inputClassName}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>

              <div className="mb-5">
                <label htmlFor="forgot-captcha" className={labelClassName}>
                  Captcha <FaAsterisk className="mt-0.5 text-[8px] text-rose-400" aria-hidden="true" />
                </label>
                <div className="flex flex-col gap-2 sm:flex-row">
                  <input
                    id="forgot-captcha"
                    type="text"
                    placeholder="Enter captcha"
                    className={inputClassName}
                    value={captchaInput}
                    onChange={(e) => setCaptchaInput(e.target.value)}
                    required
                  />
                  <div className="flex min-w-[148px] items-center justify-between rounded-xl bg-blue-600 px-4 py-3 font-semibold tracking-[0.22em] text-white sm:justify-center sm:gap-3">
                    <span className="text-sm">{captcha}</span>
                    <button
                      type="button"
                      onClick={refreshCaptcha}
                      title="Refresh Captcha"
                      className="rounded-full bg-white/10 px-2 py-1 text-sm tracking-normal transition hover:bg-white/20"
                    >
                      ↻
                    </button>
                  </div>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full rounded-xl bg-blue-600 px-4 py-3 text-[15px] font-semibold text-white transition hover:bg-blue-500 disabled:cursor-not-allowed disabled:bg-slate-600"
              >
                {loading ? "Sending..." : "Send Password Reset Link"}
              </button>

              <div className="mt-5 text-center text-sm text-slate-400">
                Remember your password?{" "}
                <Link
                  to="/login"
                  className="font-semibold text-blue-400 transition hover:text-blue-300"
                >
                  Login
                </Link>
              </div>
            </form>
          </div>
        </section>

        <Footer />
    </main>
  );
};

export default ForgetPass;
